import { useState, useEffect } from "react";
import { modalSvg, toastSvg, warningSvg } from "./svgPaths";
import { PrimaryButton, SecondaryButton } from "./shared";

// ─── Confirmation Dialog (generic) ─────────────────────────────────────────────

export function ConfirmDialog({ title, body, confirmLabel = "Confirm", cancelLabel = "Cancel", onConfirm, onCancel }) {
  const [closeHover, setCloseHover] = useState(false);
  return (
    <div
      onClick={onCancel}
      style={{ position: "fixed", inset: 0, backgroundColor: "rgba(7,46,101,0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 120, padding: "var(--spacing-20, 20px)" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: "var(--bg-colors-default-primary, #ffffff)", boxShadow: "0px 4px 4px rgba(21,92,180,0.06), 0px 16px 16px rgba(21,92,180,0.08)", borderRadius: "var(--border-radius-lg, 8px)", padding: "var(--spacing-24, 24px)", width: 540, maxWidth: "100%", display: "flex", flexDirection: "column", gap: "var(--spacing-24, 24px)" }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-20, 20px)", width: "100%" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", width: "100%" }}>
            <div style={{ display: "flex", alignItems: "flex-start", padding: "var(--spacing-12, 12px)", borderRadius: "var(--border-radius-circle, 200px)", backgroundColor: "var(--accent-warning-extra-b, #fcf5f0)", flexShrink: 0 }}>
              <div style={{ position: "relative", width: 24, height: 24 }}>
                <svg style={{ position: "absolute", inset: "9.4% 5%" }} fill="none" viewBox="0 0 21.5941 19.4877" preserveAspectRatio="none">
                  <path clipRule="evenodd" d={warningSvg.p1fca4480} fill="var(--accent-warning-primary, #d07200)" fillRule="evenodd" />
                  <path clipRule="evenodd" d={warningSvg.pb976e80} fill="var(--accent-warning-primary, #d07200)" fillRule="evenodd" />
                  <path clipRule="evenodd" d={warningSvg.p1ad5700} fill="var(--accent-warning-primary, #d07200)" fillRule="evenodd" />
                </svg>
              </div>
            </div>
            <button
              type="button"
              onClick={onCancel}
              aria-label="Close dialog"
              onMouseEnter={() => setCloseHover(true)}
              onMouseLeave={() => setCloseHover(false)}
              style={{ width: 36, height: 36, borderRadius: "var(--border-radius-sm, 4px)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", border: "none", padding: 8, backgroundColor: closeHover ? "var(--bg-colors-hover-primary, #f4f7fc)" : "transparent", transition: "background-color 0.15s ease", flexShrink: 0 }}
            >
              <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 13.607 13.607" preserveAspectRatio="none">
                <path clipRule="evenodd" d={toastSvg.p33964000} fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" transform="rotate(45 6.8035 6.8035)" />
              </svg>
            </button>
          </div>
          <p style={{ fontSize: "var(--font-size-18, 1.125rem)", lineHeight: "28px", color: "var(--eyp-color-blue-900-dark-blue, #072e65)", margin: 0, width: "100%" }}>{title}</p>
        </div>

        <p style={{ fontWeight: 300, fontSize: "var(--font-size-16, 1rem)", lineHeight: "24px", color: "var(--text-colors-default-primary, #2e2e38)", margin: 0, width: "100%" }}>{body}</p>

        <div style={{ display: "flex", gap: "var(--spacing-12, 12px)", alignItems: "center", justifyContent: "flex-end", width: "100%" }}>
          <SecondaryButton onClick={onCancel} style={{ height: 36 }}>{cancelLabel}</SecondaryButton>
          <PrimaryButton onClick={onConfirm} style={{ height: 36 }}>{confirmLabel}</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

// ─── Feedback Modal (shared by Like & Dislike) ─────────────────────────────────

const RESPONSE_OPTIONS = [
  "Accurate and helpful",
  "Partially correct",
  "Incorrect information",
  "Not relevant",
  "Other",
];

function DSSelect({ label, placeholder, options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const borderColor = open
    ? "var(--border-colors-focus-primary, #1a9afa)"
    : hover
    ? "var(--border-colors-hover-primary, #afcdfb)"
    : "var(--border-colors-default-secondary, #afcdfb)";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-6, 6px)", width: "100%" }}>
      <span style={{ fontWeight: 300, fontSize: "var(--font-size-14, 0.875rem)", lineHeight: "20px", color: "var(--text-colors-default-primary, #2e2e38)", textAlign: "left" }}>{label}</span>
      <div style={{ position: "relative", width: "100%" }}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{ width: "100%", height: 44, borderRadius: "var(--border-radius-sm, 4px)", border: `${open ? 2 : 1}px solid ${borderColor}`, backgroundColor: "var(--bg-colors-default-primary, #ffffff)", padding: open ? "3px 7px 3px 13px" : "4px 8px 4px 14px", display: "flex", alignItems: "center", gap: "var(--spacing-8, 8px)", cursor: "pointer", boxSizing: "border-box", transition: "border-color 0.15s ease" }}
        >
          <span style={{ flex: "1 0 0", minWidth: 0, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 300, fontSize: "var(--font-size-14, 0.875rem)", lineHeight: "20px", color: value ? "var(--text-colors-default-primary, #2e2e38)" : "var(--text-colors-default-secondary, #656579)" }}>{value || placeholder}</span>
          <div style={{ width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s ease" }}>
            <svg style={{ width: 11.25, height: 6.25 }} fill="none" viewBox="0 0 11.25 6.25" preserveAspectRatio="none">
              <path clipRule="evenodd" d={modalSvg.p2a5779b0} fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" />
            </svg>
          </div>
        </button>
        {open && (
          <>
            <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 30 }} />
            <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, backgroundColor: "var(--bg-colors-default-primary, #ffffff)", border: "1px solid var(--border-colors-default-primary, #d8e5fd)", borderRadius: "var(--border-radius-sm, 4px)", boxShadow: "0px 4px 16px color-mix(in srgb, var(--shadow-colors-primary, #155cb4) 14%, transparent)", padding: "var(--spacing-4, 4px)", display: "flex", flexDirection: "column", zIndex: 40, maxHeight: 220, overflowY: "auto" }}>
              {options.map((opt) => {
                const active = opt === value;
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => { onChange(opt); setOpen(false); }}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--spacing-8, 8px)", padding: "var(--spacing-8, 8px) var(--spacing-12, 12px)", borderRadius: "var(--border-radius-sm, 4px)", border: "none", backgroundColor: active ? "var(--bg-colors-hover-secondary, #d8e5fd)" : "transparent", cursor: "pointer", textAlign: "left", fontWeight: 300, fontSize: "var(--font-size-14, 0.875rem)", lineHeight: "20px", color: "var(--text-colors-default-primary, #2e2e38)", transition: "background-color 0.15s ease" }}
                    onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = "var(--bg-colors-hover-secondary, #d8e5fd)"; }}
                    onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = "transparent"; }}
                  >
                    <span style={{ flex: "1 0 0", minWidth: 0 }}>{opt}</span>
                    {active && (
                      <div style={{ flexShrink: 0 }}>
                        <CheckGlyph color="var(--icon-colors-selected-default-primary, #064372)" size={20} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function DSTextArea({ label, placeholder, value, onChange, helper, maxLength = 2000 }) {
  const [focused, setFocused] = useState(false);
  const [hover, setHover] = useState(false);
  const borderColor = focused
    ? "var(--border-colors-focus-primary, #1a9afa)"
    : hover
    ? "var(--border-colors-hover-primary, #afcdfb)"
    : "var(--border-colors-default-secondary, #afcdfb)";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-6, 6px)", width: "100%" }}>
      <span style={{ fontWeight: 300, fontSize: "var(--font-size-14, 0.875rem)", lineHeight: "20px", color: "var(--text-colors-default-primary, #2e2e38)", textAlign: "left" }}>{label}</span>
      <div style={{ borderRadius: "var(--border-radius-sm, 4px)", border: `${focused ? 2 : 1}px solid ${borderColor}`, backgroundColor: "var(--bg-colors-default-primary, #ffffff)", padding: focused ? "7px 11px" : "var(--spacing-8, 8px) var(--spacing-12, 12px)", transition: "border-color 0.15s ease" }}>
        <textarea
          value={value}
          maxLength={maxLength}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          placeholder={placeholder}
          aria-label={label}
          style={{ width: "100%", height: 80, border: "none", outline: "none", resize: "none", background: "transparent", fontWeight: 300, fontSize: "var(--font-size-14, 0.875rem)", lineHeight: "20px", color: "var(--text-colors-default-primary, #2e2e38)", boxSizing: "border-box", display: "block" }}
        />
      </div>
      {helper && (
        <span style={{ fontWeight: 300, fontSize: "var(--font-size-12, 0.75rem)", lineHeight: "18px", color: "var(--text-colors-default-secondary, #656579)" }}>{helper}</span>
      )}
    </div>
  );
}

export function FeedbackModal({ onClose, onSubmit }) {
  const [selected, setSelected] = useState("");
  const [message, setMessage] = useState("");
  const [closeHover, setCloseHover] = useState(false);

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, backgroundColor: "rgba(7,46,101,0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: "var(--spacing-20, 20px)" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: "var(--bg-colors-default-primary, #ffffff)", boxShadow: "0px 4px 4px rgba(21,92,180,0.06), 0px 16px 16px rgba(21,92,180,0.08)", borderRadius: "var(--border-radius-lg, 8px)", padding: "var(--spacing-20, 20px)", width: 440, maxWidth: "100%", display: "flex", flexDirection: "column", gap: "var(--spacing-12, 12px)" }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: "var(--spacing-8, 8px)" }}>
          <p style={{ flex: "1 0 0", fontSize: "var(--font-size-18, 1.125rem)", lineHeight: "28px", color: "var(--eyp-color-blue-900-dark-blue, #072e65)", margin: 0 }}>Submit Feedback</p>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close feedback"
            onMouseEnter={() => setCloseHover(true)}
            onMouseLeave={() => setCloseHover(false)}
            style={{ width: 36, height: 36, borderRadius: "var(--border-radius-sm, 4px)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", border: "none", padding: 8, backgroundColor: closeHover ? "var(--bg-colors-default-extra-a, #f4f7fc)" : "transparent", transition: "background-color 0.15s ease", flexShrink: 0 }}
          >
            <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 13.607 13.607" preserveAspectRatio="none">
              <path clipRule="evenodd" d={toastSvg.p33964000} fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" transform="rotate(45 6.8035 6.8035)" />
            </svg>
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-16, 16px)", width: "100%", borderTop: "1px solid var(--border-colors-default-primary, #d8e5fd)", paddingTop: "var(--spacing-16, 16px)" }}>
          <DSSelect
            label="Select your response"
            placeholder="Select"
            options={RESPONSE_OPTIONS}
            value={selected}
            onChange={setSelected}
          />
          <DSTextArea
            label="Write additional response (if any)"
            placeholder="Message"
            value={message}
            onChange={setMessage}
          />
        </div>

        <div style={{ height: 1, width: "100%", backgroundColor: "var(--border-colors-default-primary, #d8e5fd)" }} />

        <div style={{ display: "flex", gap: "var(--spacing-12, 12px)", alignItems: "center", justifyContent: "flex-end", width: "100%" }}>
          <SecondaryButton onClick={onClose} style={{ height: 36 }}>Cancel</SecondaryButton>
          <PrimaryButton onClick={() => onSubmit({ category: selected, comment: message })} disabled={!selected} style={{ height: 36 }}>Submit</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

// ─── Feedback Toast (top-right, auto-dismiss in 2s) ────────────────────────────

export function FeedbackToast({ onClose }) {
  const [closeHover, setCloseHover] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const showT = setTimeout(() => setVisible(true), 10);
    const fadeT = setTimeout(() => setVisible(false), 1700);
    const closeT = setTimeout(onClose, 2000);
    return () => { clearTimeout(showT); clearTimeout(fadeT); clearTimeout(closeT); };
  }, [onClose]);

  return (
    <div style={{ position: "fixed", top: "var(--spacing-20, 20px)", right: "var(--spacing-20, 20px)", zIndex: 200, width: 360, maxWidth: "calc(100vw - 40px)", opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(-8px)", transition: "opacity 0.3s ease, transform 0.3s ease" }}>
      <div style={{ backgroundColor: "#e7fce8", border: "1px solid #189d3e", boxShadow: "0px 4px 4px rgba(21,92,180,0.06), 0px 16px 16px rgba(21,92,180,0.08)", borderRadius: "var(--border-radius-lg, 8px)", padding: "var(--spacing-16, 16px)", display: "flex", flexDirection: "column", gap: "var(--spacing-12, 12px)" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", width: "100%" }}>
          <div style={{ position: "relative", width: 20, height: 20, flexShrink: 0 }}>
            <svg style={{ position: "absolute", inset: "5.21%" }} fill="none" viewBox="0 0 17.9167 17.9167" preserveAspectRatio="none">
              <path clipRule="evenodd" d={toastSvg.p1169cb00} fill="#168736" fillRule="evenodd" />
              <path clipRule="evenodd" d={toastSvg.p3a335780} fill="#168736" fillRule="evenodd" />
            </svg>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close toast"
            onMouseEnter={() => setCloseHover(true)}
            onMouseLeave={() => setCloseHover(false)}
            style={{ width: 20, height: 20, borderRadius: "var(--border-radius-sm, 4px)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", border: "none", padding: 0, backgroundColor: closeHover ? "rgba(24,157,62,0.15)" : "transparent", transition: "background-color 0.15s ease", flexShrink: 0 }}
          >
            <svg style={{ width: 12, height: 12 }} fill="none" viewBox="0 0 13.607 13.607" preserveAspectRatio="none">
              <path clipRule="evenodd" d={toastSvg.p33964000} fill="#13652a" fillRule="evenodd" transform="rotate(45 6.8035 6.8035)" />
            </svg>
          </button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-4, 4px)", width: "100%" }}>
          <p style={{ fontSize: "var(--font-size-14, 0.875rem)", lineHeight: "20px", color: "#13652a", margin: 0 }}>Feedback submitted</p>
          <p style={{ fontWeight: 300, fontSize: "var(--font-size-14, 0.875rem)", lineHeight: "20px", color: "#13652a", margin: 0 }}>Thank you. Your feedback has been submitted successfully.</p>
        </div>
      </div>
    </div>
  );
}

export function CheckGlyph({ color = "#ffffff", size = 16 }) {
  return (
    <svg style={{ width: size, height: size }} fill="none" viewBox="4 4.5 10 9" preserveAspectRatio="xMidYMid meet">
      <path clipRule="evenodd" d={toastSvg.p1169cb00} fill={color} fillRule="evenodd" />
    </svg>
  );
}

export function CloseGlyph({ color = "var(--eyp-color-blue-900-dark-blue, #072e65)", size = 14 }) {
  return (
    <svg style={{ width: size, height: size }} fill="none" viewBox="0 0 13.607 13.607" preserveAspectRatio="none">
      <path clipRule="evenodd" d={toastSvg.p33964000} fill={color} fillRule="evenodd" transform="rotate(45 6.8035 6.8035)" />
    </svg>
  );
}

export function CheckIcon() {
  return (
    <div style={{ position: "relative", width: 16, height: 16 }}>
      <svg style={{ position: "absolute", inset: "13%", width: "74%", height: "74%" }} fill="none" viewBox="4 4.5 10 9" preserveAspectRatio="xMidYMid meet">
        <path clipRule="evenodd" d={toastSvg.p1169cb00} fill="var(--eyp-color-green-600, #168736)" fillRule="evenodd" />
      </svg>
    </div>
  );
}
